import * as React from "react";
import { motion } from "motion/react";
import { cn } from "../lib/cn";

/**
 * EmptyState — for "you have nothing yet" surfaces.
 *
 * Wired with the AIKO voice by default: warm, slightly self-aware, never
 * patronizing. Override `title` and `description` per surface; the defaults
 * are sensible for "lists / collections of stuff this user could create".
 *
 * Composition:
 * - `icon` — typically a lucide icon, sized 48-64px in a soft tinted circle
 * - `title` — short heading (default: "Nothing here yet.")
 * - `description` — one-line subtext (default: "First one's the hardest. After that, the rest just show up.")
 * - `action` — usually a Button to start the creation flow
 */
export interface EmptyStateProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title" | "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"> {
  icon?: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** When true, animates in on mount via Framer (subtle fade + lift) */
  animate?: boolean;
}

const DEFAULT_TITLE = "Nothing here yet.";
const DEFAULT_DESCRIPTION = "First one's the hardest. After that, the rest just show up.";

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ className, icon, title = DEFAULT_TITLE, description = DEFAULT_DESCRIPTION, action, animate = true, ...props }, ref) => {
    const wrapperClass = cn(
      "flex flex-col items-center justify-center text-center px-6 py-12 gap-4",
      "rounded-2xl border border-dashed border-input bg-background",
      className,
    );
    const inner = (
      <>
        {icon ? (
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted text-muted-foreground">
            {icon}
          </div>
        ) : null}
        <div className="space-y-1.5 max-w-sm">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
        </div>
        {action ? <div className="pt-1">{action}</div> : null}
      </>
    );

    if (animate) {
      return (
        <motion.div
          ref={ref}
          className={wrapperClass}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
        >
          {inner}
        </motion.div>
      );
    }

    return (
      <div ref={ref} className={wrapperClass} {...props}>
        {inner}
      </div>
    );
  },
);
EmptyState.displayName = "EmptyState";
